import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Rating,
} from "@mui/material";

import Seo from "../components/Seo";
import { SITE } from "../data/site";
import { services } from "../data/services";
import { reviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildItemListSchema, buildWebPageSchema } from "../utils/schema";
import "./Home.css";

export default function Home() {
  const safeServices = Array.isArray(services) ? services : [];
  const safeReviews = Array.isArray(reviews) ? reviews : [];
  const title = "Brainwave Solutions — Software, Data, Spreadsheets & Workflow Support";
  const description =
    "Practical help with spreadsheets, dashboards, automation, data cleanup, reporting, websites, and custom tools for businesses and individuals.";
  const canonical = "https://brainwavesolutions.net/";

  const heroUrl = `${process.env.PUBLIC_URL}/assets/hero-illustration.svg`;
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([{ name: "Home", url: canonical }]),
    buildItemListSchema({
      name: "Brainwave Solutions services",
      items: safeServices.map((service) => ({
        name: service.title,
        url: `${SITE.url}${service.uri}`,
      })),
    }),
    {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "@id": `${SITE.url}/#professional-service`,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5",
        reviewCount: String(safeReviews.length),
        bestRating: "5",
        worstRating: "1",
      },
      review: safeReviews.slice(0, 3).map((review) => ({
        "@type": "Review",
        reviewRating: {
          "@type": "Rating",
          ratingValue: "5",
          bestRating: "5",
        },
        author: {
          "@type": "Person",
          name: review.author,
        },
        reviewBody: review.quote,
        itemReviewed: {
          "@id": `${SITE.url}/#professional-service`,
        },
      })),
      provider: {
        "@id": `${SITE.url}/#professional-service`,
      },
    },
  ];

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        image={`${SITE.url}/assets/hero-illustration.svg`}
        structuredData={structuredData}
      />

      {/* HERO */}
      <Box className="bws-hero">
        <Container maxWidth="lg">
          <Grid
            container
            className="bws-heroGrid"
            spacing={4}
            alignItems="center"
          >
            <Grid item xs={12} sm={7} className="bws-heroCopyCol">
              <div className="bws-heroCopy">
                <p className="bws-eyebrow">Practical business support</p>

                <Typography variant="h1" className="bws-heroTitle">
                  Clean up the work that has become messy, manual, or harder than it should be.
                </Typography>

                <p className="bws-heroLead">
                  Brainwave Solutions helps businesses and individuals fix spreadsheets, organize data, improve
                  reporting, automate repetitive work, and build practical tools that make day-to-day operations easier.
                </p>

                <p className="bws-heroLead bws-heroLeadSecondary">
                  Whether you need a quick fix, ongoing support, or a better system altogether, the goal is the same:
                  clearer workflows, more reliable information, and less friction in the work.
                </p>

                <div className="bws-heroCtas">
                  <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                    Contact
                  </Button>
                  <Button component={RouterLink} to="/services" variant="outlined" className="bws-btnGhost">
                    View services
                  </Button>
                </div>

                <div className="bws-heroTrust">
                  <Chip label="20+ years of experience" className="bws-pill" />
                  <Chip label="Direct communication" className="bws-pill" />
                  <Chip label="Practical solutions" className="bws-pill" />
                </div>
              </div>
            </Grid>

            <Grid item xs={12} sm={5} className="bws-heroArtCol">
              <Paper className="bws-heroArt" elevation={0}>
                <img
                  className="bws-heroImg"
                  src={heroUrl}
                  alt="Illustration showing reporting, analytics, and connected workflow data"
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* SERVICES */}
      <Box id="services" className="bws-section">
        <Container maxWidth="lg">
          <div className="bws-sectionHead">
            <Typography variant="h2" className="bws-sectionTitle">
              Services
            </Typography>
            <p className="bws-muted">
              Support for spreadsheets, reporting, automation, websites, operations, and the systems behind the work.
            </p>
          </div>

          <div className="bws-cardsGrid">
            {safeServices.map((s) => (
              <Card key={s.title} className="bws-card" elevation={0}>
                <CardContent
                  className="bws-cardContent"
                  component={RouterLink}
                  to={s.uri}
                  sx={{ textDecoration: "none", color: "inherit", display: "block" }}
                >
                  <Typography className="bws-cardTitle">{s.title}</Typography>
                  <Typography className="bws-cardText">{s.blurb}</Typography>

                  <ul className="bws-cardList">
                    {(Array.isArray(s.bullets) ? s.bullets : []).map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <Paper className="bws-ctaBand" elevation={0}>
            <div className="bws-ctaDiv">
              <Typography className="bws-ctaTitle">Not sure which service fits?</Typography>
              <p className="bws-muted">
                Send a quick description of your project, and let us know how we can assist you.
              </p>
            </div>

            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              className="bws-btn"
            >
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* REVIEWS */}
      <Box id="reviews" className="bws-sectionAlt">
        <Container maxWidth="lg">
          <div className="bws-sectionHead">
            <Typography variant="h2" className="bws-sectionTitle">
              Client feedback
            </Typography>
          </div>

          <div className="bws-reviewsGrid">
            {safeReviews.map((r, idx) => (
              <Paper key={idx} className="bws-review" elevation={0}>
                <div className="bws-stars">
                  <Rating value={5} readOnly />
                </div>
                <blockquote className="bws-quote">“{r.quote}”</blockquote>
                <div className="bws-quoteBy">— {r.author}</div>
              </Paper>
            ))}
          </div>

          <div className="bws-microCta">
            <Button
              component={RouterLink}
              to="/contact"
              variant="outlined"
              className="bws-btnGhost"
            >
              Contact
            </Button>
          </div>
        </Container>
      </Box>
    </>
  );
}
